/** Shared spritesheet layout constants and facing-direction utilities for Player and Enemy sprites. */

// Spritesheet layout: 7 columns × 3 rows
// Rows: south (0), north (1), east (2). West = east flipped at runtime.
// Cols: idle (0), walk-1 (1), walk-2 (2), attack-windup (3), attack-strike (4), hit (5), death (6)

export const COLS_PER_ROW = 7;
export const COL_IDLE = 0;
export const COL_WALK1 = 1;
export const COL_WALK2 = 2;
export const COL_ATTACK_WINDUP = 3;
export const COL_ATTACK_STRIKE = 4;
export const COL_HIT = 5;
export const COL_DEATH = 6;
export const ROW_SOUTH = 0;
export const ROW_NORTH = 1;
export const ROW_EAST = 2;
export const ANIM_FRAME_RATE = 8;

/** Display size in pixels for standard (light/medium) entity sprites. */
export const SPRITE_DISPLAY_STANDARD = 96;
/** Display size in pixels for heavy/immovable entity sprites. */
export const SPRITE_DISPLAY_HEAVY = 128;

// --- Sprite body geometry (source-space coordinates within 512×512 frames) ---
// These define where the physics body sits within the sprite frame so that the
// body's bottom edge aligns with the character's visual feet.

/** Source frame size (all sprite sheets use 512×512 frames). */
export const FRAME_SIZE = 512;

/**
 * Compute body.setSize() and body.setOffset() values for a scaled sprite.
 * Ensures the physics body's bottom edge aligns with the character's feet,
 * preventing visual overlap with wall tiles.
 *
 * @param displayBodyW Desired body width in display pixels (e.g. 16)
 * @param displayBodyH Desired body height in display pixels (e.g. 24)
 * @param feetY Y coordinate of character's feet in source frame (e.g. 376)
 * @param centerX X coordinate of character's center in source frame (e.g. 256)
 * @param scale Sprite display scale (displaySize / FRAME_SIZE)
 */
export function computeBodyGeometry(
  displayBodyW: number,
  displayBodyH: number,
  feetY: number,
  centerX: number,
  scale: number,
): { sizeW: number; sizeH: number; offsetX: number; offsetY: number } {
  // setSize() values are in source coords; Phaser multiplies by scale internally
  const sizeW = Math.round(displayBodyW / scale);
  const sizeH = Math.round(displayBodyH / scale);
  // Center body horizontally on character, bottom edge at feet
  const offsetX = Math.round(centerX - sizeW / 2);
  const offsetY = Math.round(feetY - sizeH);
  return { sizeW, sizeH, offsetX, offsetY };
}

// Pre-computed geometries for each entity type (avoid recalculating per-instance)
// Player: 512px frame, feet at y=376, center at x=256, display 96px, body 16×24
export const PLAYER_BODY = computeBodyGeometry(16, 24, 376, 256, SPRITE_DISPLAY_STANDARD / FRAME_SIZE);
// Enemy light/medium: 512px frame, feet at y=372, center at x=257, display 96px, body 14×14
export const ENEMY_LIGHT_BODY = computeBodyGeometry(14, 14, 372, 257, SPRITE_DISPLAY_STANDARD / FRAME_SIZE);
// Enemy heavy/immovable: same sprite, display 128px, body 20×20
export const ENEMY_HEAVY_BODY = computeBodyGeometry(20, 20, 372, 257, SPRITE_DISPLAY_HEAVY / FRAME_SIZE);

/** Resolve a facing vector to a spritesheet row, cardinal direction, and flipX flag. */
export function facingToRow(fx: number, fy: number): { row: number; dir: string; flipX: boolean } {
  if (Math.abs(fx) > Math.abs(fy)) {
    return { row: ROW_EAST, dir: 'east', flipX: fx < 0 };
  } else if (fy < 0) {
    return { row: ROW_NORTH, dir: 'north', flipX: false };
  }
  return { row: ROW_SOUTH, dir: 'south', flipX: false };
}
